// AI Pentest tab - Delta orchestrator UI.
//
// State machine view: shows whatever the engagement currently is doing and
// surfaces the right operator action. Polls every 5s while the engagement is
// in a non-terminal status so the operator sees steps complete in near real
// time without manually refreshing.
//
// Sub-views:
//   StartForm       - no engagement yet; operator picks goal preset + starts
//   PlanReview      - status=reviewing, plan is editable + approve/regenerate
//   ExecutionPanel  - status=executing, step timeline + stop button
//   EscalationPanel - status=awaiting_approval, decide modal
//   CompletePanel   - status=complete, report + audit
//   StoppedPanel    - status=stopped|failed, audit
//
// Audit and Plan-snapshot are accessible from any non-Start state via tabs.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  AlertTriangle, Atom, Bot, Bug, CheckCircle, ChevronDown, ChevronUp, Compass,
  FileCode2, FileText, Fingerprint, FolderSearch, Globe, History, Loader2,
  Lock, Octagon, PauseCircle, PlayCircle, Radar, RefreshCw, Sparkles, Square,
  Terminal, Wrench, X,
} from 'lucide-react';

import { orchestratorAPI } from '../../../../services/api';
import { useAuth } from '../../../../contexts/AuthContext';
import { EmptyState, PrimaryCTA } from '../../../common/Shinobi';
import ConfirmModal from '../../../common/ConfirmModal';
import { parseServerDate } from '../../../../utils/time';

const TERMINAL_STATUSES = new Set(['complete', 'stopped', 'failed']);
const POLL_INTERVAL_MS = 5000;

const STATUS_BADGE = {
  planning:           'bg-slate-100 text-slate-700 border-slate-300',
  reviewing:          'bg-amber-100 text-amber-700 border-amber-300',
  executing:          'bg-wasabi-100 text-wasabi-700 border-wasabi-300',
  awaiting_approval:  'bg-purple-100 text-purple-700 border-purple-300',
  complete:           'bg-emerald-100 text-emerald-700 border-emerald-300',
  stopped:            'bg-orange-100 text-orange-700 border-orange-300',
  failed:             'bg-red-100 text-red-700 border-red-300',
};

const STATUS_LABEL = {
  planning:           'Planning',
  reviewing:          'Awaiting your review',
  executing:          'Running',
  awaiting_approval:  'Awaiting your decision',
  complete:           'Complete',
  stopped:            'Stopped',
  failed:             'Failed',
};

const GOAL_PRESET_LABEL = {
  owasp_top_10:    'OWASP Top 10',
  external_recon:  'External Recon',
  api_security:    'API Security',
  custom:          'Custom',
};

// Tool metadata: friendly label, lucide icon, accent color family.
// Accent values map to Tailwind classes inside ToolPill below.
const TOOL_META = {
  subdomain_enum:   { label: 'Subdomain Enum',   icon: Globe,        accent: 'cyan' },
  web_crawler:      { label: 'Web Crawler',      icon: Compass,      accent: 'cyan' },
  tech_fingerprint: { label: 'Tech Fingerprint', icon: Fingerprint,  accent: 'cyan' },
  headers_check:    { label: 'Headers',          icon: FileCode2,    accent: 'cyan' },
  tls_check:        { label: 'TLS',              icon: Lock,         accent: 'cyan' },
  shodan_scan:      { label: 'Shodan',           icon: Radar,        accent: 'cyan' },
  dir_bruteforce:   { label: 'Dir Bruteforce',   icon: FolderSearch, accent: 'cyan' },
  nuclei_scan:      { label: 'Nuclei',           icon: Atom,         accent: 'green' },
  zap_scan:         { label: 'ZAP',              icon: Bug,          accent: 'orange' },
  kali_command:     { label: 'Kali',             icon: Terminal,     accent: 'purple' },
};

const TOOL_ACCENT = {
  cyan:   'bg-cyan-100 text-cyan-700 border-cyan-300',
  green:  'bg-wasabi-100 text-wasabi-700 border-wasabi-300',
  purple: 'bg-purple-100 text-purple-700 border-purple-300',
  orange: 'bg-orange-100 text-orange-700 border-orange-300',
  slate:  'bg-slate-100 text-slate-700 border-slate-300',
};

const PHASE_LABEL = {
  recon:              'Recon',
  enumeration:        'Enum',
  vulnerability_scan: 'Vuln Scan',
  verification:       'Verify',
};

const PHASE_ACCENT = {
  recon:              'bg-blue-100 text-blue-700 border-blue-300',
  enumeration:        'bg-amber-100 text-amber-700 border-amber-300',
  vulnerability_scan: 'bg-orange-100 text-orange-700 border-orange-300',
  verification:       'bg-emerald-100 text-emerald-700 border-emerald-300',
};


function ToolPill({ tool, params }) {
  const meta = TOOL_META[tool] || { label: tool, icon: Wrench, accent: 'slate' };
  const Icon = meta.icon;
  const accent = TOOL_ACCENT[meta.accent] || TOOL_ACCENT.slate;
  const binary = (params && tool === 'kali_command') ? params.binary : null;
  // Wave C autonomous follow-through steps are kali_command rows stamped with
  // params.__wave_c__ at planning time. Tag them so the operator can tell at
  // a glance which steps the planner generated vs which came from the original
  // plan. Same pill, plus a purple "Wave C" chip after the binary name.
  const isWaveC = !!(params && params.__wave_c__);
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-medium border ${accent} shrink-0`}>
      <Icon className="w-3.5 h-3.5" />
      <span>{meta.label}</span>
      {binary && (
        <>
          <span className="text-slate-500">·</span>
          <span className="font-mono">{binary}</span>
        </>
      )}
      {isWaveC && (
        <>
          <span className="text-slate-500">·</span>
          <span className="inline-flex items-center gap-0.5 px-1 rounded bg-purple-100 text-purple-700 border border-purple-300 font-bold uppercase tracking-wider text-[9px]">
            Wave C
          </span>
        </>
      )}
    </span>
  );
}


function PhasePill({ phase }) {
  const label = PHASE_LABEL[phase] || phase.replace(/_/g, ' ');
  const accent = PHASE_ACCENT[phase] || 'bg-cyber-100/50 text-slate-700 border-cyber-300/30';
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider border ${accent} shrink-0`}>
      {label}
    </span>
  );
}


// Revision badge with a styled hover popover. Native title is unreliable
// (slow, plain, sometimes clipped); a CSS-only group-hover popover renders
// instantly and matches the rest of the UI.
function RevisionBadge({ version }) {
  return (
    <span className="relative inline-block group">
      <span className="text-xs text-slate-500 font-normal cursor-help border-b border-dotted border-slate-500/60 hover:border-slate-400 hover:text-slate-800 transition">
        Revision {version}
      </span>
      <span
        role="tooltip"
        className="
          pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-2 z-20
          opacity-0 group-hover:opacity-100 transition-opacity duration-150
          w-72 rounded-lg border border-cyber-300/50 bg-cyber-50 shadow-xl
          px-3 py-2 text-xs text-slate-700 leading-relaxed
        "
      >
        <span className="block text-slate-500">
          Plan revision counter. <span className="text-slate-800">Revision 1</span> is the initial draft from Wasabi Shinobi AI. The number bumps each time you regenerate the plan or approve an edited version.
        </span>
      </span>
    </span>
  );
}

const STEP_STATUS_BADGE = {
  pending:    'bg-slate-100 text-slate-700 border-slate-300',
  dispatched: 'bg-blue-100 text-blue-700 border-blue-300',
  running:    'bg-wasabi-100 text-wasabi-700 border-wasabi-300',
  completed:  'bg-emerald-100 text-emerald-700 border-emerald-300',
  skipped:    'bg-slate-100 text-slate-500 border-slate-400/20',
  failed:     'bg-red-100 text-red-700 border-red-300',
  escalated:  'bg-purple-100 text-purple-700 border-purple-300',
};


export default function AIPentest({ pentest }) {
  const { id: pentestIdParam } = useParams();
  const pentestId = Number(pentestIdParam);
  const { isPentester } = useAuth();

  const [engagement, setEngagement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [view, setView] = useState('main');
  const [confirmStop, setConfirmStop] = useState(false);
  const [showEscalation, setShowEscalation] = useState(false);
  // When the latest engagement is in a terminal state, the operator can hit
  // "Start a new engagement" which flips this flag and renders the StartForm
  // even though there is still a previous engagement on file.
  const [forceStartForm, setForceStartForm] = useState(false);

  const fetchLatest = useCallback(async () => {
    try {
      const list = await orchestratorAPI.listForPentest(pentestId);
      if (!list || list.length === 0) {
        setEngagement(null);
        setError(null);
        return;
      }
      const latest = list[0];
      const detail = await orchestratorAPI.get(latest.id);
      setEngagement(detail);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, [pentestId]);

  useEffect(() => { fetchLatest(); }, [fetchLatest]);

  // Poll while non-terminal so steps stream in near real time.
  useEffect(() => {
    if (!engagement) return;
    if (TERMINAL_STATUSES.has(engagement.status)) return;
    const t = setInterval(fetchLatest, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [engagement, fetchLatest]);

  const handleStart = async (form) => {
    setBusy('starting');
    setError(null);
    try {
      const detail = await orchestratorAPI.create(pentestId, {
        pentest_id: pentestId,
        goal_preset: form.goal_preset,
        goal_freeform: form.goal_freeform || null,
      });
      setEngagement(detail);
      setForceStartForm(false);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setBusy(null);
    }
  };

  const handleStartNew = () => {
    setForceStartForm(true);
    setView('main');
    setError(null);
  };

  const handleRegenerate = async () => {
    if (!engagement) return;
    setBusy('regenerating');
    setError(null);
    try {
      const detail = await orchestratorAPI.regenerate(engagement.id);
      setEngagement(detail);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setBusy(null);
    }
  };

  const handleApprove = async (editedPlan) => {
    if (!engagement) return;
    setBusy('approving');
    setError(null);
    try {
      const payload = editedPlan ? { plan: { steps: editedPlan } } : {};
      const detail = await orchestratorAPI.approve(engagement.id, payload);
      setEngagement(detail);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.errors) {
        setError(`${detail.message}: ${detail.errors.map(e => `seq=${e.sequence} ${e.field}: ${e.message}`).join('; ')}`);
      } else {
        setError(detail || err.message);
      }
    } finally {
      setBusy(null);
    }
  };

  const handleStop = async () => {
    if (!engagement) return;
    setConfirmStop(false);
    setBusy('stopping');
    setError(null);
    try {
      const detail = await orchestratorAPI.stop(engagement.id);
      setEngagement(detail);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setBusy(null);
    }
  };

  if (loading) return <div className="text-slate-500">Loading orchestrator...</div>;

  // While the synchronous create+generate-plan call is in flight (~10-20s),
  // show the planning loader immediately instead of leaving the StartForm
  // visible with a "Generating..." button. The real engagement object arrives
  // when the LLM call returns and we transition straight into PlanReview.
  if (busy === 'starting') {
    return (
      <div className="space-y-6">
        {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
        <PlanGenerationLoader />
      </div>
    );
  }

  // No engagement yet, OR operator clicked "Start new engagement" on a terminal one.
  if (!engagement || forceStartForm) {
    return (
      <div className="space-y-6">
        {error && <ErrorBanner message={error} />}
        {!isPentester ? (
          <EmptyState icon={Bot} accent="green" title="AI Pentest"
            description="Only Pentesters can start an AI orchestrated engagement." />
        ) : (
          <StartForm
            pentest={pentest}
            onStart={handleStart}
            busy={busy === 'starting'}
            onCancel={engagement ? () => setForceStartForm(false) : null}
          />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

      <EngagementHeader
        engagement={engagement}
        onRefresh={fetchLatest}
        onStop={() => setConfirmStop(true)}
        canStop={isPentester && !TERMINAL_STATUSES.has(engagement.status)}
        stopping={busy === 'stopping'}
        canStartNew={false}   /* retired: new engagements start on the Bench */
        onStartNew={handleStartNew}
      />

      <ViewTabs current={view} onChange={setView} engagement={engagement} />

      {view === 'main' && (
        <MainPanel
          engagement={engagement}
          isPentester={isPentester}
          busy={busy}
          onApprove={handleApprove}
          onRegenerate={handleRegenerate}
          onOpenEscalation={() => setShowEscalation(true)}
        />
      )}
      {view === 'audit' && <AuditView engagementId={engagement.id} />}
      {view === 'report' && <ReportView engagement={engagement} />}

      <ConfirmModal
        isOpen={confirmStop}
        onClose={() => setConfirmStop(false)}
        onConfirm={handleStop}
        title="Stop AI Pentest engagement?"
        message="The executor will halt at the next step boundary. In-flight scanner tasks already on a beacon may still complete; any pending steps stay un-executed. This action cannot be undone."
        confirmText="Stop engagement"
        cancelText="Keep running"
        variant="warning"
      />

      {showEscalation && engagement.pending_decision && (
        <EscalationModal
          engagement={engagement}
          onClose={() => setShowEscalation(false)}
          onResolved={(detail) => { setShowEscalation(false); setEngagement(detail); }}
        />
      )}
    </div>
  );
}


// ----- Header / status / nav -----


function ErrorBanner({ message, onDismiss }) {
  return (
    <div className="rounded-lg border border-red-300 bg-red-100 p-3 text-sm text-red-700 flex items-start gap-2">
      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
      <div className="flex-1">{String(message)}</div>
      {onDismiss && (
        <button onClick={onDismiss} className="text-red-700/70 hover:text-red-700">
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}


function EngagementHeader({ engagement, onRefresh, onStop, canStop, stopping, canStartNew, onStartNew }) {
  const badge = STATUS_BADGE[engagement.status] || STATUS_BADGE.planning;
  const inPct = pct(engagement.input_tokens_used, engagement.input_token_budget);
  const outPct = pct(engagement.output_tokens_used, engagement.output_token_budget);
  return (
    <div className="rounded-2xl border border-cyber-300/40 bg-cyber-50/60 p-5">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl border border-wasabi-300 bg-wasabi-100 flex items-center justify-center text-shinobi-green">
            <Bot className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Engagement #{engagement.id}</div>
            <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
              AI Pentest
              <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded border ${badge}`}>
                {STATUS_LABEL[engagement.status] || engagement.status.replace(/_/g, ' ')}
              </span>
              <RevisionBadge version={engagement.plan_version} />
            </h3>
            <div className="text-xs text-slate-500 mt-0.5">
              Goal: {GOAL_PRESET_LABEL[engagement.goal_preset] || engagement.goal_preset}
              {engagement.goal_freeform ? ` - ${engagement.goal_freeform.slice(0, 80)}` : ''}
            </div>
            <div className="text-[11px] text-slate-500 mt-1 font-mono">
              {engagement.llm_provider} - planner: {engagement.model_planner} - reporter: {engagement.model_reporter}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onRefresh}
            className="text-slate-500 hover:text-slate-800 p-2 rounded-lg hover:bg-cyber-100/50"
            title="Refresh engagement state"
            aria-label="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          {canStop && (
            <button
              onClick={onStop}
              disabled={stopping}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border border-orange-300 text-orange-700 hover:bg-orange-100 disabled:opacity-50"
            >
              <Square className="w-4 h-4" />
              {stopping ? 'Stopping...' : 'Stop'}
            </button>
          )}
          {canStartNew && (
            <button
              onClick={onStartNew}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border border-wasabi-300 text-shinobi-green hover:bg-wasabi-100 transition"
            >
              <PlayCircle className="w-4 h-4" />
              Start new engagement
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
        <BudgetMeter label="Input tokens" used={engagement.input_tokens_used} cap={engagement.input_token_budget} pct={inPct} />
        <BudgetMeter label="Output tokens" used={engagement.output_tokens_used} cap={engagement.output_token_budget} pct={outPct} />
        <ElapsedDisplay engagement={engagement} />
      </div>
    </div>
  );
}


function BudgetMeter({ label, used, cap, pct }) {
  const color = pct > 90 ? 'bg-red-500/60' : pct > 70 ? 'bg-amber-500/60' : 'bg-shinobi-green/50';
  return (
    <div className="rounded-lg border border-cyber-300/30 bg-cyber-100/40 p-3">
      <div className="flex items-center justify-between mb-1.5">
        <span className="uppercase tracking-wider text-slate-500 text-[10px] font-semibold">{label}</span>
        <span className="font-mono text-slate-700">{(used || 0).toLocaleString()} / {(cap || 0).toLocaleString()}</span>
      </div>
      <div className="h-1.5 rounded-full bg-cyber-300/20 overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
    </div>
  );
}


// Elapsed-time display. The engagement has no wall-clock cap any more (LLM
// safety is enforced by token budgets, scanner safety by per-step timeout).
// We just show how long it has been running so the operator has context.
// If the orchestrator detects no progress in 6h the backend pauses the
// engagement to AWAITING_APPROVAL, which the EscalationPanel handles.
function ElapsedDisplay({ engagement }) {
  const startD = parseServerDate(engagement.started_at);
  const endD = parseServerDate(engagement.completed_at);
  const elapsed = startD ? Math.max(0, (Date.now() - startD.getTime()) / 1000) : 0;
  const completed = startD && endD ? Math.max(0, (endD.getTime() - startD.getTime()) / 1000) : null;
  const display = completed ?? elapsed;
  const isTerminal = ['complete', 'stopped', 'failed'].includes(engagement.status);
  return (
    <div className="rounded-lg border border-cyber-300/30 bg-cyber-100/40 p-3">
      <div className="flex items-center justify-between mb-1">
        <span className="uppercase tracking-wider text-slate-500 text-[10px] font-semibold">
          {isTerminal ? 'Total runtime' : 'Elapsed'}
        </span>
        <span className="font-mono text-slate-700">{fmtDuration(display)}</span>
      </div>
      <div className="text-[10px] text-slate-500 leading-tight">
        {isTerminal && startD
          ? `Started ${startD.toLocaleString()}`
          : 'No wall-clock cap. Pauses for review if no progress in 6h.'}
      </div>
    </div>
  );
}


function ViewTabs({ current, onChange, engagement }) {
  const tabs = [
    { id: 'main', label: 'Plan & Steps', icon: Sparkles },
    { id: 'audit', label: 'Audit', icon: History },
    ...(engagement.final_report ? [{ id: 'report', label: 'Report', icon: FileText }] : []),
  ];
  return (
    <nav className="flex gap-1 rounded-xl bg-cyber-100/60 p-1 border border-cyber-300/40 w-fit">
      {tabs.map(({ id, label, icon: Icon }) => (
        <button
          key={id}
          onClick={() => onChange(id)}
          className={`flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs transition ${
            current === id
              ? 'bg-wasabi-100 text-wasabi-700 border border-wasabi-300'
              : 'text-slate-700 hover:text-slate-900 hover:bg-cyber-200/50 border border-transparent'
          }`}
        >
          <Icon className="w-3.5 h-3.5" /> {label}
        </button>
      ))}
    </nav>
  );
}


// ----- Start form -----


function StartForm() {
  // Plan-then-execute is retired. This used to be the form that started one; it
  // is now the signpost that says where to go instead. Kept in place rather than
  // deleted so an operator landing on a bookmarked URL is told what happened,
  // instead of finding an empty tab.
  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6">
      <h3 className="text-base font-semibold text-amber-900">
        Plan-then-execute engagements are retired
      </h3>
      <p className="mt-2 max-w-2xl text-sm leading-relaxed text-amber-900/90">
        This mode fixed six to fifteen steps up front, before the first result came
        back, and capped its follow-up pass at twenty validations for the whole
        engagement. It could not pursue a lead. The <strong>Bench</strong> tab
        replaces it: one agent per hypothesis, each working until it can say
        confirmed or refuted, and no verdict becomes a finding until it survives
        an independent review.
      </p>
      <p className="mt-2 text-xs text-amber-800">
        Engagements already run here stay readable below. Nothing has been deleted.
      </p>
    </div>
  );
}

function PlanGenerationLoader() {
  return (
    <div className="rounded-xl border border-cyber-300/40 bg-cyber-50/60 p-8 text-center">
      <Loader2 className="w-8 h-8 text-shinobi-green mx-auto mb-3 animate-spin" />
      <p className="text-sm text-slate-700">Wasabi Shinobi AI is generating the initial plan...</p>
      <p className="text-xs text-slate-500 mt-1">This usually takes 10-20 seconds.</p>
    </div>
  );
}


function MainPanel({ engagement, isPentester, busy, onApprove, onRegenerate, onOpenEscalation }) {
  if (engagement.status === 'planning') {
    return <PlanGenerationLoader />;
  }
  if (engagement.status === 'reviewing') {
    return (
      <PlanReview
        engagement={engagement}
        canApprove={isPentester}
        busy={busy}
        onApprove={onApprove}
        onRegenerate={onRegenerate}
      />
    );
  }
  if (engagement.status === 'awaiting_approval') {
    return (
      <EscalationPanel engagement={engagement} onOpen={onOpenEscalation} />
    );
  }
  if (engagement.status === 'executing') {
    return <ExecutionTimeline engagement={engagement} />;
  }
  // complete | stopped | failed
  return <CompletePanel engagement={engagement} />;
}


// ----- Plan review (status=reviewing) -----


function PlanReview({ engagement, canApprove, busy, onApprove, onRegenerate }) {
  const snap = engagement.plan_snapshot || {};
  const [editing, setEditing] = useState(false);
  const [steps, setSteps] = useState(() => (snap.steps || []).map((s, i) => ({ ...s, sequence: s.sequence ?? i + 1 })));

  useEffect(() => {
    setSteps((snap.steps || []).map((s, i) => ({ ...s, sequence: s.sequence ?? i + 1 })));
  }, [snap.steps]);

  const handleApproveAsIs = () => onApprove(null);
  const handleApproveEdited = () => onApprove(steps);
  const updateStep = (idx, patch) => setSteps(prev => prev.map((s, i) => i === idx ? { ...s, ...patch } : s));
  const removeStep = (idx) => setSteps(prev => prev.filter((_, i) => i !== idx).map((s, i) => ({ ...s, sequence: i + 1 })));

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-amber-300 bg-amber-100 p-4 text-sm text-amber-700 flex items-start gap-3">
        <PauseCircle className="w-4 h-4 mt-0.5 shrink-0" />
        <div>
          Plan is ready for your review. Approve to dispatch as-is, or edit step params/targets first.
          Regenerate to ask Wasabi Shinobi AI for a different draft.
        </div>
      </div>

      {snap.overall_strategy && (
        <div className="rounded-xl border border-cyber-300/40 bg-cyber-50/60 p-4">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-1.5">Strategy</div>
          <p className="text-sm text-slate-800 leading-relaxed">{snap.overall_strategy}</p>
        </div>
      )}

      <div className="rounded-xl border border-cyber-300/40 bg-cyber-50/60 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-cyber-300/30">
          <div className="text-xs uppercase tracking-wider text-slate-500">
            Plan ({steps.length} step{steps.length === 1 ? '' : 's'})
          </div>
          {canApprove && (
            <button
              type="button"
              onClick={() => setEditing((e) => !e)}
              className="text-xs text-slate-500 hover:text-shinobi-green"
            >
              {editing ? 'Done editing' : 'Edit plan'}
            </button>
          )}
        </div>
        <div className="divide-y divide-cyber-300/20">
          {steps.map((s, i) => (
            <PlanStepRow key={i} step={s} editable={editing} onChange={(p) => updateStep(i, p)} onRemove={() => removeStep(i)} />
          ))}
        </div>
      </div>

      {canApprove && (
        <div className="flex items-center justify-between gap-3 pt-2 pb-6">
          <button
            type="button"
            onClick={onRegenerate}
            disabled={!!busy}
            className="text-sm text-slate-500 hover:text-shinobi-green inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${busy === 'regenerating' ? 'animate-spin' : ''}`} />
            {busy === 'regenerating' ? 'Regenerating...' : 'Regenerate plan'}
          </button>
          <PrimaryCTA
            variant="green"
            icon={PlayCircle}
            iconPosition="left"
            loading={busy === 'approving'}
            onClick={editing ? handleApproveEdited : handleApproveAsIs}
          >
            {editing ? 'Approve edited plan' : 'Approve & start'}
          </PrimaryCTA>
        </div>
      )}
    </div>
  );
}


function PlanStepRow({ step, editable, onChange, onRemove }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="px-4 py-2.5">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full text-left flex items-start gap-3">
        <span className="font-mono text-xs text-slate-500 mt-1 w-6 shrink-0">#{step.sequence}</span>
        <div className="mt-0.5"><PhasePill phase={step.phase} /></div>
        <div className="mt-0.5"><ToolPill tool={step.tool} params={step.params} /></div>
        <span className="flex-1 min-w-0">
          <span className="text-sm text-slate-900 block truncate">{step.target}</span>
          {step.rationale && (
            <span className="text-xs text-slate-500 block truncate">{step.rationale}</span>
          )}
        </span>
        {open ? <ChevronUp className="w-4 h-4 text-slate-500 mt-1" /> : <ChevronDown className="w-4 h-4 text-slate-500 mt-1" />}
      </button>

      {open && (
        <div className="ml-9 mt-3 space-y-2 text-xs">
          {!editable && (
            <>
              {step.expected_signal && (
                <div><span className="text-slate-500 uppercase tracking-wider mr-2">Expected:</span><span className="text-slate-700">{step.expected_signal}</span></div>
              )}
              <div><span className="text-slate-500 uppercase tracking-wider mr-2">Params:</span><code className="text-shinobi-green/80 font-mono">{JSON.stringify(step.params || {})}</code></div>
            </>
          )}
          {editable && (
            <div className="space-y-2">
              <Field label="Tool" value={step.tool} onChange={(v) => onChange({ tool: v })} />
              <Field label="Target" value={step.target} onChange={(v) => onChange({ target: v })} />
              <Field label="Target type" value={step.target_type || 'url'} onChange={(v) => onChange({ target_type: v })} />
              <Field label="Rationale" value={step.rationale || ''} onChange={(v) => onChange({ rationale: v })} multiline />
              <Field label="Expected signal" value={step.expected_signal || ''} onChange={(v) => onChange({ expected_signal: v })} multiline />
              <Field label="Params (JSON)" value={JSON.stringify(step.params || {})} onChange={(v) => {
                try { onChange({ params: JSON.parse(v) }); } catch { /* user is mid-edit, ignore */ }
              }} multiline />
              <button type="button" onClick={onRemove} className="text-xs text-red-600 hover:text-red-700 inline-flex items-center gap-1">
                <X className="w-3 h-3" /> Remove step
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function Field({ label, value, onChange, multiline = false }) {
  const Cmp = multiline ? 'textarea' : 'input';
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">{label}</label>
      <Cmp
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded bg-cyber-100/50 border border-cyber-300/30 text-xs text-slate-900 px-2 py-1 focus:border-shinobi-green/60 focus:outline-none"
        rows={multiline ? 2 : undefined}
      />
    </div>
  );
}


// ----- Execution timeline (status=executing) -----


function ExecutionTimeline({ engagement }) {
  const steps = engagement.steps || [];
  if (steps.length === 0) {
    return <EmptyState icon={Loader2} title="Spinning up..." description="Steps will appear here as the executor dispatches them." />;
  }
  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-wasabi-300 bg-shinobi-green/5 p-3 text-xs text-slate-700 flex items-center gap-2">
        <Loader2 className="w-3.5 h-3.5 text-shinobi-green animate-spin" />
        Executor running. UI refreshes every {POLL_INTERVAL_MS / 1000}s.
      </div>
      <div className="rounded-xl border border-cyber-300/40 bg-cyber-50/60 divide-y divide-cyber-300/20">
        {steps.map((s) => <StepTimelineRow key={s.id} step={s} />)}
      </div>
    </div>
  );
}


function StepTimelineRow({ step }) {
  const [open, setOpen] = useState(false);
  const badge = STEP_STATUS_BADGE[step.status] || STEP_STATUS_BADGE.pending;
  return (
    <div className="px-4 py-3">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full text-left flex items-start gap-3">
        <span className="font-mono text-xs text-slate-500 mt-1 w-6 shrink-0">#{step.sequence}</span>
        <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider border mt-0.5 ${badge}`}>
          {step.status}
        </span>
        <div className="mt-0.5"><ToolPill tool={step.tool} params={step.params} /></div>
        <span className="flex-1 min-w-0">
          <span className="text-sm text-slate-900 block truncate">{step.target}</span>
          <span className="text-xs text-slate-500 block">
            {step.findings_count > 0 ? `${step.findings_count} finding${step.findings_count === 1 ? '' : 's'} - ` : ''}
            {step.dispatched_at ? `dispatched ${relTime(step.dispatched_at)}` : 'pending'}
            {step.completed_at ? ` - finished ${relTime(step.completed_at)}` : ''}
          </span>
        </span>
        {open ? <ChevronUp className="w-4 h-4 text-slate-500 mt-1" /> : <ChevronDown className="w-4 h-4 text-slate-500 mt-1" />}
      </button>
      {open && (
        <div className="ml-9 mt-3 space-y-2 text-xs">
          {step.rationale && <div><span className="text-slate-500 uppercase tracking-wider mr-2">Rationale:</span><span className="text-slate-700">{step.rationale}</span></div>}
          {step.expected_signal && <div><span className="text-slate-500 uppercase tracking-wider mr-2">Expected:</span><span className="text-slate-700">{step.expected_signal}</span></div>}
          {step.result_summary && <div><span className="text-slate-500 uppercase tracking-wider mr-2">Result:</span><span className="text-slate-700">{step.result_summary}</span></div>}
          {step.claude_review && <div><span className="text-slate-500 uppercase tracking-wider mr-2">Review:</span><span className="text-slate-700">{step.claude_review}</span></div>}
          {step.error && <div><span className="text-red-600 uppercase tracking-wider mr-2">Error:</span><span className="text-red-700">{step.error}</span></div>}
          {step.beacon_task_id && <div><span className="text-slate-500 uppercase tracking-wider mr-2">Beacon task:</span><code className="text-slate-500 font-mono">{step.beacon_task_id}</code></div>}
          {step.tool === 'kali_command' && step.beacon_task_id && (
            <KaliRawOutputViewer taskId={step.beacon_task_id} />
          )}
        </div>
      )}
    </div>
  );
}


// ----- kali_command raw output viewer (Wave B) -----
//
// Lazy-loaded panel that fetches streamed stdout/stderr lines for a beacon task
// and renders them in a monospace scroll panel with a copy button. Shown only
// for kali_command steps inside StepTimelineRow.
function KaliRawOutputViewer({ taskId }) {
  const [shown, setShown] = useState(false);
  const [lines, setLines] = useState([]);
  const [total, setTotal] = useState(0);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [copied, setCopied] = useState(false);

  const fetchLines = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const data = await orchestratorAPI.taskOutput(taskId, { afterSeq: 0, limit: 5000 });
      setLines(data?.lines || []);
      setTotal(data?.total || 0);
      setTruncated(!!data?.truncated);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  const onToggle = () => {
    if (!shown) {
      setShown(true);
      if (lines.length === 0) fetchLines();
    } else {
      setShown(false);
    }
  };

  const onCopy = async () => {
    const text = lines.map((l) => `${l.stream === 'stderr' ? 'ERR ' : '    '}${l.line}`).join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard API unavailable; ignore
    }
  };

  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={onToggle}
        className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-slate-500 hover:text-shinobi-green border border-cyber-300/40 hover:border-wasabi-300 rounded px-2 py-1 transition"
      >
        <Terminal className="w-3 h-3" />
        {shown ? 'Hide raw output' : 'Show raw output'}
        {total > 0 && <span className="text-slate-500 normal-case tracking-normal">({total} line{total === 1 ? '' : 's'})</span>}
      </button>
      {shown && (
        <div className="mt-2 rounded-lg border border-cyber-300/40 bg-cyber-100/60 overflow-hidden">
          <div className="flex items-center justify-between px-3 py-1.5 border-b border-cyber-300/40 bg-cyber-200/40">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">
              {loading ? 'Loading' : truncated ? `Showing first ${lines.length} of ${total} lines (truncated)` : `${lines.length} line${lines.length === 1 ? '' : 's'}`}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={fetchLines}
                title="Refresh"
                className="text-slate-500 hover:text-slate-800"
                disabled={loading}
              >
                <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
              </button>
              <button
                type="button"
                onClick={onCopy}
                disabled={lines.length === 0}
                className="text-[10px] uppercase tracking-wider text-slate-500 hover:text-shinobi-green disabled:opacity-40"
              >
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
          </div>
          {err && <div className="px-3 py-2 text-[11px] text-red-700">{err}</div>}
          {!err && lines.length === 0 && !loading && (
            <div className="px-3 py-3 text-[11px] text-slate-500">No output captured.</div>
          )}
          {!err && lines.length > 0 && (
            <pre className="text-[11px] font-mono text-slate-800 leading-snug px-3 py-2 overflow-auto max-h-96 whitespace-pre-wrap break-all">
              {lines.map((l) => (
                <div key={l.seq} className={l.stream === 'stderr' ? 'text-rose-700' : 'text-slate-800'}>
                  {l.line}
                </div>
              ))}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}


// ----- Escalation (status=awaiting_approval) -----


function EscalationPanel({ engagement, onOpen }) {
  const dec = engagement.pending_decision;
  const summary = describeEscalation(dec);
  return (
    <div className="rounded-xl border border-purple-300 bg-purple-100 p-5">
      <div className="flex items-start gap-3 mb-4">
        <Octagon className="w-5 h-5 text-purple-700 mt-0.5 shrink-0" />
        <div className="flex-1">
          <h3 className="text-base font-semibold text-purple-700">Wasabi Shinobi AI is asking for your approval</h3>
          <p className="text-sm text-slate-700 mt-1">
            {summary.headline}
          </p>
          <p className="text-xs text-slate-500 mt-1">
            If you do not respond within 4 hours, the executor auto-declines and continues with the rest of the plan.
          </p>
        </div>
      </div>

      {dec?.rationale && (
        <div className="rounded-lg border border-purple-300 bg-cyber-50/40 p-3 mb-4">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-1.5">Why</div>
          <p className="text-sm text-slate-800">{dec.rationale}</p>
        </div>
      )}

      {summary.proposedSteps.length > 0 && (
        <div className="rounded-lg border border-purple-300 bg-cyber-50/40 p-3 mb-4">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-2">
            Proposed step{summary.proposedSteps.length === 1 ? '' : 's'} to insert ({summary.proposedSteps.length})
          </div>
          <div className="space-y-2">
            {summary.proposedSteps.map((s, i) => <ProposedStepRow key={i} step={s} />)}
          </div>
        </div>
      )}

      {summary.requestedAction && (
        <div className="rounded-lg border border-purple-300 bg-cyber-50/40 p-3 mb-4">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-1.5">Requested action</div>
          <p className="text-sm text-slate-800">{summary.requestedAction}</p>
          {summary.justification && (
            <p className="text-xs text-slate-500 mt-2">{summary.justification}</p>
          )}
        </div>
      )}

      <div className="flex justify-end">
        <PrimaryCTA variant="green" icon={CheckCircle} iconPosition="left" onClick={onOpen}>
          Review and decide
        </PrimaryCTA>
      </div>
    </div>
  );
}


// Translate a raw OrchestratedDecision into something the operator can scan in 3 seconds.
function describeEscalation(dec) {
  if (!dec) return { headline: 'Pending decision', proposedSteps: [], requestedAction: null, justification: null };
  const t = dec.decision_type;
  const p = dec.payload || {};

  if (t === 'insert' && Array.isArray(p.steps) && p.steps.length > 0) {
    const tools = p.steps.map((s) => (TOOL_META[s.tool]?.label) || s.tool);
    const unique = Array.from(new Set(tools));
    return {
      headline: `Wants to add ${p.steps.length} step${p.steps.length === 1 ? '' : 's'} to the plan (${unique.join(', ')}).`,
      proposedSteps: p.steps,
      requestedAction: null,
      justification: null,
    };
  }
  if (t === 'escalate') {
    return {
      headline: p.requested_action ? `Wants to do something outside the plan rails: ${p.requested_action}` : 'Wants to do something outside the plan rails.',
      proposedSteps: [],
      requestedAction: p.requested_action || null,
      justification: p.justification || null,
    };
  }
  return {
    headline: `Decision: ${t}`,
    proposedSteps: Array.isArray(p.steps) ? p.steps : [],
    requestedAction: p.requested_action || null,
    justification: p.justification || null,
  };
}


function ProposedStepRow({ step }) {
  return (
    <div className="rounded border border-cyber-300/30 bg-cyber-100/30 p-2.5 text-sm">
      <div className="flex items-start gap-2 mb-1.5 flex-wrap">
        <PhasePill phase={step.phase} />
        <ToolPill tool={step.tool} params={step.params} />
        <span className="text-slate-800 font-medium">{step.target}</span>
      </div>
      {step.rationale && <div className="text-xs text-slate-500 mt-1">{step.rationale}</div>}
      {step.params && Object.keys(step.params).length > 0 && step.tool !== 'kali_command' && (
        <div className="text-[11px] text-slate-500 mt-1">
          <span className="uppercase tracking-wider mr-1">Params:</span>
          <code className="font-mono text-slate-500">{JSON.stringify(step.params)}</code>
        </div>
      )}
      {step.tool === 'kali_command' && step.params?.args && (
        <div className="text-[11px] text-slate-500 mt-1">
          <span className="uppercase tracking-wider mr-1">Args:</span>
          <code className="font-mono text-slate-500">{(step.params.args || []).join(' ')}</code>
        </div>
      )}
    </div>
  );
}


function EscalationModal({ engagement, onClose, onResolved }) {
  const dec = engagement.pending_decision;
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const summary = describeEscalation(dec);
  const isInsert = (dec?.decision_type === 'insert') && summary.proposedSteps.length > 0;

  const handle = async (approve) => {
    setBusy(true);
    setErr(null);
    try {
      const payload = approve
        ? { decision_id: dec.id, note, plan: { steps: summary.proposedSteps } }
        : { decision_id: dec.id, note };
      const detail = await orchestratorAPI.decideEscalation(engagement.id, payload);
      onResolved(detail);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === 'object' && d.errors) {
        setErr(`${d.message} ${d.errors.map((x) => `seq=${x.sequence} ${x.field}: ${x.message}`).join('; ')}`);
      } else {
        setErr(d || e.message);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-cyber-50 border border-cyber-300/40 rounded-2xl max-w-3xl w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start gap-3 mb-4">
          <Octagon className="w-5 h-5 text-purple-700 mt-0.5 shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Wasabi Shinobi AI is asking for your approval</h3>
            <p className="text-sm text-slate-500 mt-1">{summary.headline}</p>
          </div>
        </div>

        {dec?.rationale && (
          <div className="rounded-lg border border-cyber-300/40 bg-cyber-100/40 p-3 mb-4">
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-1.5">Why</div>
            <p className="text-sm text-slate-800">{dec.rationale}</p>
          </div>
        )}

        {isInsert && (
          <div className="rounded-lg border border-cyber-300/40 bg-cyber-100/40 p-3 mb-4">
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-2">
              Steps that will be added to the plan if you approve
            </div>
            <div className="space-y-2">
              {summary.proposedSteps.map((s, i) => <ProposedStepRow key={i} step={s} />)}
            </div>
          </div>
        )}

        {summary.requestedAction && (
          <div className="rounded-lg border border-amber-300 bg-amber-100 p-3 mb-4">
            <div className="text-[10px] uppercase tracking-[0.18em] text-amber-700 font-semibold mb-1.5">Out-of-rails action requested</div>
            <p className="text-sm text-amber-100">{summary.requestedAction}</p>
            {summary.justification && (
              <p className="text-xs text-amber-700/80 mt-2">{summary.justification}</p>
            )}
          </div>
        )}

        <div className="rounded-lg border border-slate-600/40 bg-cyber-100/30 p-3 mb-4 text-xs text-slate-500 space-y-1">
          <div><span className="text-slate-500 mr-1">Approve:</span> the proposed steps are validated against scope and the tool palette, then spliced into the plan; execution resumes immediately.</div>
          <div><span className="text-slate-500 mr-1">Decline:</span> nothing is added; the executor continues with whatever pending steps remain.</div>
        </div>

        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={2}
          placeholder="Optional note explaining your decision (recorded in audit)"
          className="w-full rounded-lg bg-cyber-100/50 border border-cyber-300/40 text-sm text-slate-900 px-3 py-2 placeholder:text-slate-500 focus:border-shinobi-green/60 focus:outline-none"
        />
        {err && <div className="mt-2 text-sm text-red-700">{String(err)}</div>}
        <div className="flex justify-between items-center mt-4">
          <button onClick={onClose} className="text-sm text-slate-500 hover:text-slate-800">Cancel</button>
          <div className="flex gap-2">
            <button
              onClick={() => handle(false)}
              disabled={busy}
              className="px-3 py-1.5 rounded-lg text-sm border border-slate-300 text-slate-700 hover:bg-slate-100 disabled:opacity-50"
            >
              Decline
            </button>
            <PrimaryCTA
              variant="green"
              icon={CheckCircle}
              iconPosition="left"
              onClick={() => handle(true)}
              loading={busy}
              disabled={!isInsert && !summary.requestedAction}
            >
              {isInsert ? `Approve and add ${summary.proposedSteps.length} step${summary.proposedSteps.length === 1 ? '' : 's'}` : 'Approve'}
            </PrimaryCTA>
          </div>
        </div>
      </div>
    </div>
  );
}


// ----- Complete / Stopped panel -----


function CompletePanel({ engagement }) {
  const steps = engagement.steps || [];
  const summary = useMemo(() => {
    const counts = { completed: 0, skipped: 0, failed: 0, total: steps.length, findings: 0 };
    for (const s of steps) {
      if (s.status === 'completed') counts.completed++;
      if (s.status === 'skipped') counts.skipped++;
      if (s.status === 'failed') counts.failed++;
      counts.findings += s.findings_count || 0;
    }
    return counts;
  }, [steps]);

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-cyber-300/40 bg-cyber-50/60 p-5">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <Stat label="Total steps" value={summary.total} />
          <Stat label="Completed" value={summary.completed} accent="emerald" />
          <Stat label="Skipped" value={summary.skipped} accent="slate" />
          <Stat label="Findings" value={summary.findings} accent="green" />
        </div>
        {engagement.error && (
          <div className="mt-4 rounded-lg border border-red-300 bg-red-100 p-3 text-sm text-red-700">
            {engagement.error}
          </div>
        )}
      </div>

      <div className="rounded-xl border border-cyber-300/40 bg-cyber-50/60 divide-y divide-cyber-300/20">
        {steps.map((s) => <StepTimelineRow key={s.id} step={s} />)}
      </div>
    </div>
  );
}


function Stat({ label, value, accent }) {
  const colors = {
    emerald: 'text-emerald-700',
    slate: 'text-slate-700',
    green: 'text-shinobi-green',
  };
  return (
    <div className="rounded-lg border border-cyber-300/30 bg-cyber-100/40 p-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className={`text-2xl font-semibold mt-1 ${colors[accent] || 'text-slate-900'}`}>{value}</div>
    </div>
  );
}


// ----- Audit view -----


function AuditView({ engagementId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    orchestratorAPI.audit(engagementId)
      .then((d) => { if (!cancelled) setRows(d || []); })
      .catch((e) => { if (!cancelled) setErr(e?.response?.data?.detail || e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [engagementId]);

  if (loading) return <div className="text-slate-500 text-sm">Loading audit...</div>;
  if (err) return <ErrorBanner message={err} />;

  return (
    <div className="rounded-xl border border-cyber-300/40 bg-cyber-50/60 divide-y divide-cyber-300/20">
      {rows.length === 0 ? (
        <div className="p-6 text-center text-slate-500 text-sm">No audit entries.</div>
      ) : rows.map((r) => <AuditRow key={r.id} row={r} />)}
    </div>
  );
}


function AuditRow({ row }) {
  const [open, setOpen] = useState(false);
  const kindColor = {
    prompt: 'bg-blue-100 text-blue-700 border-blue-300',
    response: 'bg-wasabi-100 text-wasabi-700 border-wasabi-300',
    dispatch: 'bg-amber-100 text-amber-700 border-amber-300',
    operator_action: 'bg-purple-100 text-purple-700 border-purple-300',
    system: 'bg-slate-100 text-slate-700 border-slate-300',
  };
  const KIND_LABEL = {
    prompt: 'Prompt',
    response: 'Response',
    dispatch: 'Dispatch',
    operator_action: 'Operator',
    system: 'System',
  };
  return (
    <div className="px-4 py-2.5">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full text-left flex items-start gap-3">
        <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider border ${kindColor[row.kind] || kindColor.system} shrink-0 mt-0.5`}>
          {KIND_LABEL[row.kind] || row.kind.replace(/_/g, ' ')}
        </span>
        <span className="flex-1 min-w-0">
          <span className="text-sm text-slate-900 block truncate">{row.label || '(no label)'}</span>
          <span className="text-[11px] text-slate-500">
            {relTime(row.created_at)}
            {row.input_tokens != null ? ` - input ${row.input_tokens.toLocaleString()} tok` : ''}
            {row.output_tokens != null ? `, output ${row.output_tokens.toLocaleString()} tok` : ''}
          </span>
        </span>
        {open ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
      </button>
      {open && (
        <pre className="ml-3 mt-2 text-[11px] font-mono text-slate-500 whitespace-pre-wrap bg-cyber-100/30 rounded p-2 max-h-96 overflow-auto">
          {JSON.stringify(row.payload || {}, null, 2)}
        </pre>
      )}
    </div>
  );
}


// ----- Report view -----


function ReportView({ engagement }) {
  const r = engagement.final_report;
  if (!r) {
    return <EmptyState icon={FileText} title="No report yet" description="The orchestrator generates a final report once the engagement completes." />;
  }
  const meta = r._meta || {};
  const findings = Array.isArray(r.findings) ? r.findings : [];
  const remediations = Array.isArray(r.remediation_priorities)
    ? [...r.remediation_priorities].sort((a, b) => (a.priority || 99) - (b.priority || 99))
    : [];
  const openQuestions = Array.isArray(r.open_questions) ? r.open_questions : [];
  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-cyber-300/40 bg-cyber-50/60 p-5 text-sm">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Executive Summary</div>
          {meta.model && (
            <div className="text-[11px] text-slate-500 font-mono">
              {meta.model} - input {(meta.input_tokens || 0).toLocaleString()} tok, output {(meta.output_tokens || 0).toLocaleString()} tok
              {meta.generated_at ? ` - ${relTime(meta.generated_at)}` : ''}
            </div>
          )}
        </div>
        {r.executive_summary && (
          <p className="text-slate-900 leading-relaxed">{r.executive_summary}</p>
        )}
      </div>

      {r.methodology_recap && (
        <div className="rounded-xl border border-cyber-300/40 bg-cyber-50/60 p-5 text-sm">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-2">Methodology</div>
          <p className="text-slate-700 leading-relaxed">{r.methodology_recap}</p>
        </div>
      )}

      <div className="rounded-xl border border-cyber-300/40 bg-cyber-50/60 p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
            Key Findings ({findings.length})
          </div>
        </div>
        {findings.length === 0 ? (
          <p className="text-sm text-slate-500">No notable findings.</p>
        ) : (
          <div className="space-y-2.5">
            {findings.map((f, i) => <ReportFindingRow key={i} finding={f} />)}
          </div>
        )}
      </div>

      {remediations.length > 0 && (
        <div className="rounded-xl border border-cyber-300/40 bg-cyber-50/60 p-5">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-3">
            Remediation Priorities
          </div>
          <ol className="space-y-2 list-none">
            {remediations.map((rem, i) => (
              <li key={i} className="flex gap-3 text-sm">
                <span className="text-shinobi-green font-mono text-xs mt-0.5 shrink-0 w-8">P{rem.priority}</span>
                <div className="flex-1">
                  <div className="text-slate-800">{rem.action}</div>
                  {Array.isArray(rem.addresses_finding_titles) && rem.addresses_finding_titles.length > 0 && (
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      addresses: {rem.addresses_finding_titles.join(', ')}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}

      {openQuestions.length > 0 && (
        <div className="rounded-xl border border-cyber-300/40 bg-cyber-50/60 p-5">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-3">
            Open Questions
          </div>
          <ul className="space-y-1.5 text-sm">
            {openQuestions.map((q, i) => (
              <li key={i} className="flex gap-2">
                <span className="text-amber-600 shrink-0">?</span>
                <span className="text-slate-700">{q}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}


function ReportFindingRow({ finding }) {
  const sev = (finding.severity || 'info').toLowerCase();
  const SEV_BADGE = {
    info: 'bg-slate-100 text-slate-700 border-slate-300',
    low: 'bg-blue-100 text-blue-700 border-blue-300',
    medium: 'bg-yellow-100 text-yellow-700 border-yellow-300',
    high: 'bg-orange-100 text-orange-700 border-orange-300',
    critical: 'bg-red-100 text-red-700 border-red-300',
  };
  const badge = SEV_BADGE[sev] || SEV_BADGE.info;
  return (
    <div className="rounded-lg border border-cyber-300/30 bg-cyber-100/30 p-3">
      <div className="flex items-start gap-2 mb-1.5">
        <span className={`px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded border ${badge} shrink-0`}>
          {sev}
        </span>
        <span className="text-sm font-medium text-slate-900 flex-1">{finding.title}</span>
        {Array.isArray(finding.cited_step_sequences) && finding.cited_step_sequences.length > 0 && (
          <span className="text-[11px] font-mono text-slate-500 shrink-0">
            steps {finding.cited_step_sequences.map((n) => `#${n}`).join(', ')}
          </span>
        )}
      </div>
      <div className="text-xs text-slate-500 mb-1">target: <code className="font-mono text-slate-700">{finding.target}</code></div>
      <div className="text-sm text-slate-700">{finding.description}</div>
    </div>
  );
}


// ----- Helpers -----


function pct(used, cap) {
  if (!cap || cap <= 0) return 0;
  return Math.round(((used || 0) / cap) * 100);
}


function fmtDuration(seconds) {
  if (!seconds) return '0s';
  const s = Math.floor(seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
}


function relTime(iso) {
  if (!iso) return null;
  const d = parseServerDate(iso);
  if (!d || isNaN(d.getTime())) return iso;
  const diff = Date.now() - d.getTime();
  if (diff < 30 * 1000) return 'just now';
  if (diff < 60 * 1000) return `${Math.round(diff / 1000)}s ago`;
  if (diff < 60 * 60 * 1000) return `${Math.round(diff / 60000)}m ago`;
  if (diff < 24 * 60 * 60 * 1000) return `${Math.round(diff / 3600000)}h ago`;
  return d.toLocaleString();
}
